import { useTranslation } from 'react-i18next'
import { Box, styled } from '@mui/material'
import { useIsSmallScreenFalse } from 'packages/core'
import { useApplicationPermissions } from 'hooks'
import { Filters, Button, CardViewLoader } from 'components'
import useSubcomponents from 'useSubcomponents'
import { CreateWorkflowButton } from '../CreateWorkflowButton'
import EidTableGrid from 'components/EidTableGrid/EidTableGrid'
import DeletePermission from './DeletePermission'
import { useHistory } from 'react-router'
import PermissionCard from './PermissionsCard'

const TableOverView = styled(Box)({
    padding: '16px 0px',
    '& table': {
        boxShadow: '0 4px 17px 0 rgba(0, 0, 0, 0.07)',
    },
})

const hasAccessToAddPermissionControlName =
    'ResourceAdmin-Application-AddAPIPermission-Control'

const ApplicationPermissions = ({ id, resourceId }) => {
    const { t } = useTranslation()
    const { hasAccessToControl, getAccessBasedAttributes } = useSubcomponents()
    const isSmallScreen = useIsSmallScreenFalse()
    const history = useHistory()

    const {
        list,
        loading: isLoading,
        loadingMore: isLoadingMore,
        pagination,
    } = useApplicationPermissions(id)

    const attributes = [
        {
            name: 'friendlyName',
            sortable: true,
            label: 'ApiPermissionName',
            isHeader: true,
            showInCard: false,
            //NOTE : Keeping the commented out code for subcomponent traceability
            // requireAccess: {
            //     control: 'ResourceAdmin-Application-Permissions-List-ApiPermissionName',
            // },
        },
        {
            name: 'typeFriendlyName',
            sortable: true,
            label: 'Type',
            requireAccess: {
                control: 'ResourceAdmin-Application-Permissions-List-Type',
            },
        },
        {
            name: 'consentDescription',
            sortable: true,
            label: 'Description',
            requireAccess: {
                control:
                    'ResourceAdmin-Application-Permissions-List-Description',
            },
        },
        {
            name: 'consentType',
            label: 'ConsentType',
            sortable: true,
            requireAccess: {
                control:
                    'ResourceAdmin-Application-Permissions-List-ConsentType',
            },
        },
        {
            hideValue: true,
            showInCard: false,
            requireAccess: {
                control: 'ResourceAdmin-Application-Permissions-List-Actions',
            },
            component: {
                componentName: 'renderer',
                render: (item) => {
                    return (
                        <Box
                            style={{
                                display: 'flex',
                                justifyContent: 'end',
                            }}
                        >
                            <Button.Details
                                item={item}
                                onClick={() => {
                                    history.push(
                                        `${history.location.search}&permissionId=${item?.id}`,
                                    )
                                }}
                            />
                            <span style={{ marginRight: '10px' }} />
                            <DeletePermission item={item} />
                        </Box>
                    )
                },
            },
        },
    ]

    return (
        <Box>
            <Box
                width="100%"
                display="flex"
                alignItems="space-between"
                justifyContent="space-between"
            >
                {hasAccessToControl(hasAccessToAddPermissionControlName) && (
                    <Box
                        data-protectedsubcomponent={
                            hasAccessToAddPermissionControlName
                        }
                    >
                        <CreateWorkflowButton
                            label={t('AddAPIPermission')}
                            workflowName="UpdateAzureAppAPIPermissions"
                            resourceId={resourceId}
                        />
                    </Box>
                )}
                <Box
                    style={{
                        margin: isSmallScreen ? '16px' : '0px 0px 0px auto',
                        width: isSmallScreen ? 'auto' : '308px',
                    }}
                >
                    <Filters.TextSearch outlined />
                </Box>
            </Box>
            <TableOverView>
                <EidTableGrid
                    noDataMessage={t('NoDataMessage')}
                    pagination={pagination}
                    isLoading={isLoading}
                    isLoadingMore={isLoadingMore}
                    attributes={getAccessBasedAttributes(attributes)}
                    list={list}
                    cardViewConfig={{
                        LoadingIndicatorComponent: CardViewLoader,
                        CardComponent: PermissionCard,
                    }}
                />
            </TableOverView>
        </Box>
    )
}

export default ApplicationPermissions
